import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getCompleteSet, getDimensions, money } from "../lib/api";
import { Reveal } from "../components/Motion";
import { useCart } from "../context/CartContext";
import { setMeta } from "../lib/seo";
import { track as trackEvent } from "../lib/analytics";

export default function CompleteSet() {
  const [set, setSet] = useState(null);
  const [dims, setDims] = useState([]);
  const { add } = useCart();

  useEffect(() => {
    window.scrollTo(0, 0);
    getCompleteSet().then((s) => {
      setSet(s);
      setMeta({
        title: "The Multidimensional Complete Set — Motion & Meaning",
        description: s.description,
        image: s.artwork || undefined,
        path: "/complete-set",
      });
    }).catch(() => setSet(false));
    getDimensions().then(setDims).catch(() => {});
    trackEvent("complete_set_view");
  }, []);

  if (set === false)
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-6">
        <p className="font-serif text-4xl">Not yet configured.</p>
        <Link to="/store" className="font-mono text-[11px] tracking-[0.2em] text-gold">← THE STORE</Link>
      </div>
    );
  if (!set) return <div className="min-h-screen" />;

  const included = (set.includedDimensions || []).map((n) => dims.find((d) => d.number === n)).filter(Boolean);

  return (
    <div data-testid="complete-set-page">
      {/* Hero */}
      <section className="relative min-h-[80svh] flex items-center bg-[#050505] overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(212,175,55,0.08),transparent_60%)]" />
        <div className="mm-vignette" />
        <div className="relative max-w-[1440px] mx-auto px-6 md:px-12 py-32 w-full">
          <Reveal>
            <p className="font-mono text-[10px] tracking-[0.5em] text-gold">THE QUALIFYING COLLECTION</p>
            <h1 className="font-serif text-[clamp(2rem,9.5vw,3.5rem)] sm:text-6xl lg:text-8xl font-light leading-[0.98] text-white mt-6 max-w-4xl">
              THE COMPLETE MULTI<wbr />DIMENSIONAL COLLECTION
            </h1>
            <p className="font-mono text-xs tracking-[0.4em] text-white/50 mt-6">{set.subtitle?.toUpperCase()}</p>
          </Reveal>
        </div>
      </section>

      {/* Included dimensions */}
      <section className="py-20 md:py-28 bg-[#070707]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <Reveal><p className="font-mono text-[11px] tracking-[0.4em] text-gold mb-12">FOUR DIMENSIONS. ONE COLLECTION.</p></Reveal>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {included.map((d, i) => (
              <Reveal key={d.number} delay={i * 0.07}>
                <div className="relative aspect-square overflow-hidden rounded-[14px] ring-1 ring-white/10" data-testid={`set-dimension-${d.number}`}>
                  {d.artwork ? (
                    <img src={d.artwork} alt={d.title} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full signal-veil bg-[#08090c] flex flex-col items-center justify-center">
                      <span className="font-serif text-6xl text-white/15">{d.number}</span>
                      <span className="font-mono text-[8px] tracking-[0.3em] text-white/35 mt-3">{d.status}</span>
                    </div>
                  )}
                  <div className="absolute bottom-0 inset-x-0 p-4 bg-gradient-to-t from-black/80 to-transparent">
                    <p className="font-mono text-[9px] tracking-[0.3em] text-white/80">{d.title}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <p className="text-white/55 leading-relaxed mt-14 max-w-2xl text-lg">{set.description}</p>
          </Reveal>
        </div>
      </section>

      {/* Dimension V eligibility */}
      <section className="py-20 md:py-28 bg-[#030303] relative overflow-hidden" data-testid="set-dv-panel">
        <div className="signal-static" />
        <div className="relative max-w-[1440px] mx-auto px-6 md:px-12">
          <Reveal>
            <p className="font-mono text-[10px] tracking-[0.45em] text-red-500/80">DIMENSION V ELIGIBILITY</p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-light text-white mt-6 max-w-3xl leading-snug">
              OWN ALL FOUR PHYSICAL DIMENSIONS — TOGETHER OR SEPARATELY — TO ENTER THE FIFTH.
            </h2>
            <p className="text-white/45 mt-6 max-w-xl leading-relaxed">
              Dimension V access is earned through verified ownership of all four physical Dimension
              albums (I–IV) — whether collected at once through the Complete Set or across separate
              orders. Qualifying collectors receive a secure access credential once their orders are
              verified and fulfilled. Access is never issued twice.
            </p>
            <Link to="/dimension-v" data-testid="set-dv-learn" className="inline-block mt-8 font-mono text-[11px] tracking-[0.25em] text-white/50 hover:text-red-400 transition-colors">
              ABOUT DIMENSION V →
            </Link>
          </Reveal>
        </div>
      </section>

      {/* Availability / purchase */}
      <section className="py-20 md:py-28 bg-[#050505]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <Reveal>
            <div className="border border-gold/20 bg-[#0a0a08] p-10 md:p-14 max-w-2xl">
              {set.available ? (
                <div data-testid="set-available">
                  <p className="font-mono text-[10px] tracking-[0.35em] text-gold mb-4">{set.preorder ? "PREORDER OPEN" : "AVAILABLE NOW"}</p>
                  <p className="font-serif text-4xl text-white">{money(set.price, set.currency)}</p>
                  {set.estimatedShipDate && (
                    <p className="font-mono text-[9px] tracking-[0.25em] text-white/40 mt-3">ESTIMATED SHIPPING: {set.estimatedShipDate}</p>
                  )}
                  <button
                    onClick={() => add({ lookupKey: set.lookupKey, name: set.name, subtitle: set.subtitle, price: set.price, currency: set.currency, artwork: set.artwork })}
                    data-testid="set-add-to-cart"
                    className="mt-8 bg-gold text-black font-mono text-[11px] tracking-[0.25em] px-10 py-4 hover:bg-white transition-colors"
                  >
                    ADD TO CART
                  </button>
                </div>
              ) : (
                <div data-testid="set-unavailable">
                  <p className="font-mono text-[10px] tracking-[0.35em] text-white/50 mb-4">NOT YET AVAILABLE</p>
                  <p className="font-serif text-3xl text-white/85">{set.releaseDateInfo || "TO BE ANNOUNCED"}</p>
                  <p className="text-white/45 mt-5 leading-relaxed">
                    The Complete Set opens when all four Dimensions have taken physical form.
                    Join the signal to be notified the moment it becomes available.
                  </p>
                  <div className="flex flex-wrap gap-5 mt-8">
                    <Link to="/join" data-testid="set-notify"
                      className="bg-gold text-black font-mono text-[11px] tracking-[0.25em] px-9 py-4 hover:bg-white transition-colors">STAY IN THE SIGNAL</Link>
                    <Link to="/dimension-v/access" data-testid="set-redeem-link"
                      className="font-mono text-[10px] tracking-[0.25em] text-white/40 hover:text-white transition-colors self-center">
                      ALREADY HOLD A KEY? REDEEM →
                    </Link>
                  </div>
                </div>
              )}
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
